import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto.ts';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { GET_CONTRIBUTOR_SUMMARY_QUERY_KEY } from '@/state/queries/contributors/user-contributors.ts';

export type DeleteContributorRoleSlotsByEventSlotDto = {
  contributorId: string;
  roleId: string;
  eventSlotId: string;
};

export const useDeleteContributorRoleSlotsByEventSlotMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DeleteContributorRoleSlotsByEventSlotDto) => {
      const token = await getToken();
      return deleteContributorRoleSlotsByEventSlot(data, token ?? '');
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] }),
        // Invalidate specific contributor summary query
        queryClient.invalidateQueries({ queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY, variables.contributorId] }),
      ]);
    },
  });
};

const deleteContributorRoleSlotsByEventSlot = async (
  data: DeleteContributorRoleSlotsByEventSlotDto,
  token: string,
): Promise<ContributorDto> => {
  const { contributorId, roleId, eventSlotId } = data;
  const url = `${import.meta.env.VITE_API_URL}/contributors/${contributorId}/roles/${roleId}/slots?eventSlotId=${eventSlotId}`;
  return sendJSONRequest({ url, method: METHOD.DELETE, body: undefined, token });
};

export default useDeleteContributorRoleSlotsByEventSlotMutation;
